
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import MetaTags from "@/components/MetaTags";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArticleHeader from "./article/ArticleHeader";
import ArticleContent from "./article/ArticleContent";
import ArticleNavigation from "./article/ArticleNavigation";
import ShareButton from "./article/ShareButton";
import CommentSection from "./article/CommentSection";
import ArticleStats from "./article/ArticleStats";
import FeedbackSection from "./article/FeedbackSection";

interface ArticleProps {
  title: string;
  author?: string;
  publishDate?: string;
  category?: string;
  readTime?: string;
  initialReadCount?: number;
  initialLikeCount?: number;
  content?: string;
  externalUrl?: string;
  previousArticleLink?: string;
  previousArticleTitle?: string;
  isPremium?: boolean;
  isPreviewMode?: boolean;
}

const Article = ({
  title,
  author = "Bharathi Priyaa",
  publishDate = "June 13, 2023",
  category = "ML Fundamentals",
  readTime = "8 min read",
  initialReadCount = 0,
  initialLikeCount = 0,
  content,
  previousArticleLink,
  previousArticleTitle,
  isPremium = false,
  isPreviewMode = false,
}: ArticleProps) => {
  // Add loading state that starts as true to prevent flash of error message
  const [isLoading, setIsLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  
  const articleId = title.toLowerCase().replace(/[^a-z0-9]/g, '-');
  
  useEffect(() => {
    if (isPremium && !isPreviewMode) {
      setShowPreview(true);
    }
    
    // Set a short timeout to simulate content loading and prevent flashing errors
    // This gives time for content to be fetched and processed
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800); // Add a slight delay to prevent error flash
    
    return () => clearTimeout(timer);
  }, [isPremium, isPreviewMode, content]);

  const breadcrumbs = [
    { name: "Home", item: "/" },
    { name: "Learn", item: "/learn" },
    { name: category, item: "/learn" },
    { name: title, item: location.pathname }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags 
        title={title}
        description={content ? content.substring(0, 160) : `Read ${title} - ${category} on HelloML`}
        articleData={{
          publishDate,
          author,
          category
        }}
        breadcrumbs={breadcrumbs}
      />
      <Navbar />
      
      <main className="flex-grow py-8 md:py-12 relative">
        <div className="container max-w-3xl">
          <div className="mb-8">
            <Link to="/learn">
              <Button variant="ghost" className="pl-0 flex items-center gap-2">
                <ArrowLeft size={16} />
                Back to resources
              </Button>
            </Link>
          </div>
          
          <article className="prose prose-lg max-w-none font-manrope relative">
            <ArticleHeader
              title={title}
              readTime={readTime}
              category={category}
              isPreviewMode={isPreviewMode}
            />
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <span>{readTime}</span>
              <span>{category}</span>
              <ArticleStats 
                articleId={articleId}
                initialReadCount={initialReadCount}
                initialLikeCount={initialLikeCount}
              />
            </div>
            
            <ArticleContent
              isLoading={isLoading}
              content={content || ''}
              showPreview={showPreview}
              isPreviewMode={isPreviewMode}
            />
            
            <div className="my-8 flex justify-end">
              <ShareButton title={title} />
            </div>
            
            <ArticleNavigation
              previousArticleLink={previousArticleLink}
              previousArticleTitle={previousArticleTitle}
            />
            
            {/* Add Feedback Section */}
            <FeedbackSection articleTitle={title} articleId={articleId} />
          </article>
          
          {/* Only show comments if not in preview mode */}
          {!showPreview && (
            <CommentSection articleId={articleId} />
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Article;
